// Canonical per-flag-type styling — single source of truth so a care flag
// renders with the SAME colour and short label everywhere (residents list,
// resident details, the add-flag form, and the mobile app which mirrors this).
//
// `short` is the compact label used in tight spaces; `color` is the accent/text
// colour and `bg` the chip background.

// `color` = accent/text (light mode), `bg` = light chip background,
// `darkColor` = a lightened accent used for text in dark mode (the raw accent
// is too dark to read on a dark surface). Dark backgrounds are derived as a
// translucent tint of `color` at runtime, so they sit correctly on any surface.
export const FLAG_TYPE_CONFIG = {
  'DNACPR':                 { short: 'DNACPR',       color: '#EF4444', bg: '#FEE2E2', darkColor: '#F87171' }, // Red
  'Allergy':                { short: 'Allergy',      color: '#F59E0B', bg: '#FEF3C7', darkColor: '#FBBF24' }, // Amber
  'DOLS / Capacity':        { short: 'DOLS',         color: '#8B5CF6', bg: '#EDE9FE', darkColor: '#A78BFA' }, // Purple
  'Mobility':               { short: 'Mobility',     color: '#3B82F6', bg: '#DBEAFE', darkColor: '#60A5FA' }, // Blue
  'Diet / Swallowing':      { short: 'Diet',         color: '#B45309', bg: '#FEF0E2', darkColor: '#FBBF24' }, // Orange-Brown
  'Falls Risk':             { short: 'Falls',        color: '#EA580C', bg: '#FFEDD5', darkColor: '#FB923C' }, // Deep Orange
  'Medication Alert':       { short: 'Meds',         color: '#DC2626', bg: '#FEE2E2', darkColor: '#F87171' }, // Red
  'Communication Need':     { short: 'Comms',        color: '#14B8A6', bg: '#CCFBF1', darkColor: '#2DD4BF' }, // Teal
  'Safeguarding Alert':     { short: 'Safeguarding', color: '#991B1B', bg: '#FEE2E2', darkColor: '#F87171' }, // Dark Red
  'Ability / Independence': { short: 'Independent',  color: '#16A34A', bg: '#DCFCE7', darkColor: '#4ADE80' }, // Green
};

const FALLBACK = { short: '', color: '#64748B', bg: '#F1F5F9', darkColor: '#94A3B8' };

// #RRGGBB → rgba() string with the given alpha.
const withAlpha = (hex, a) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/**
 * Resolve the colour + short label for a flag type.
 * Pass `isDark` (true in dark mode) so chips use a legible translucent tint and
 * a lightened accent instead of the light-mode pastel background.
 * Unknown types fall back to a neutral slate chip using the type's own name.
 */
export const flagTypeStyle = (type, isDark = false) => {
  const cfg = FLAG_TYPE_CONFIG[type] || { ...FALLBACK, short: type || 'Flag' };
  if (!isDark) return { short: cfg.short, color: cfg.color, bg: cfg.bg };
  return {
    short: cfg.short,
    color: cfg.darkColor || cfg.color,
    bg: withAlpha(cfg.color, 0.22),
  };
};
